/* eslint-disable no-shadow */
import Vue from 'vue';

export const capitalize = string => string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();

export const area = value => `${value} m²`;

/**
 * Format a number in 'cents' to a localized currency
 * ie: '199000' | currency => '19,90 €'
 *
 * @param   {String}  locale - Unicode locale identifier
 * @param   {String}  currency - Currency to use in formatting
 * @param   {Boolean} cents - Inserted value is in cents
 * @param   {String}  [currencyDisplay = 'symbol'] - How to display the currency in formatting
 * @param   {Number}  [digits = 2] - Number of digits
 * @param   {String} [style = 'currency'] - The formatting style to use
 *
 * @returns {String}                      - Formatted string
 */
export const currency = (value, {
  cents = false,
  currency = 'EUR',
  currencyDisplay = 'symbol', // symbol | code | name
  digits = 2,
  locale = 'fi-FI',
  style = 'currency', // currency | decimal,
  stripZeros = false,
} = {}) => {
  if ((!value && value !== 0) || !locale) return '-';

  const formatter = new Intl.NumberFormat(locale, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
    style,
    currency,
    currencyDisplay,
  });

  let result = formatter.format(cents ? +value / 100 : +value);

  if (stripZeros) result = result.replace(',00', '');

  return result;
};

const parseDate = (isoString) => {
  const [date, time] = isoString.split('T');
  const [year, month, day] = date.split('-');
  const [hours, minutes, seconds] = time.split(':');

  return new Date(Date.UTC(
    year,
    month - 1,
    day,
    hours,
    minutes,
    parseInt(seconds, 10),
  ));
};

const date = (value, { type = 'date', locale = 'fi-FI' } = {}) => {
  if (!value || value === '0000-00-00') return '';

  const joiner = value.match(/ \d{1}:/) ? 'T0' : 'T';
  const parsedValue = value.split(' ').join(joiner);

  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

  const types = {
    day: {
      weekday: 'short',
      timeZone,
    },
    date: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone,
    },
    month: {
      year: 'numeric',
      month: 'long',
      timeZone,
    },
    time: {
      hour: 'numeric',
      minute: 'numeric',
      timeZone,
    },
  };

  types.full = { ...types.date, ...types.time };

  const date = parseDate(new Date(parsedValue).toISOString());
  return new Intl.DateTimeFormat(locale, types[type]).format(date);
};

const filters = {
  capitalize,
  currency,
  date,
  area,
};

// Build filters from the filters object
Object.keys(filters).forEach(key => Vue.filter(key, filters[key]));

// Global filters
Vue.prototype.$filters = Vue.options.filters;

export default filters;
